import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@crisp/crisp-ui/cabbage';
import { cn } from '../../lib/cn';
import styles from './ImageLightbox.module.css';

interface ImageLightboxProps {
  src: string;
  alt: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, alt, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={styles.content}>
      <DialogTitle className="sr-only">{alt || 'Image'}</DialogTitle>
      <img src={src} alt={alt} className={styles.image} />
    </DialogContent>
  </Dialog>
);

export interface ContentImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  style?: CSSProperties;
}

/**
 * Screenshots in the docs are retina captures, so they are shown at half their
 * pixel width. Clicking one opens it at full resolution.
 */
export const ContentImage: React.FC<ContentImageProps> = ({
  src,
  alt = '',
  className = '',
  style,
  width,
  ...props
}) => {
  const ref = useRef<HTMLImageElement>(null);
  const [displayWidth, setDisplayWidth] = useState<number | null>(null);
  const [inLink, setInLink] = useState(false);
  const [open, setOpen] = useState(false);
  const hasAuthoredWidth = width !== undefined || style?.width !== undefined;

  const measure = (img: HTMLImageElement) => {
    if (!hasAuthoredWidth && img.naturalWidth > 0) {
      setDisplayWidth(Math.round(img.naturalWidth / 2));
    }
  };

  useEffect(() => {
    const img = ref.current;
    if (!img) return;
    setInLink(Boolean(img.closest('a')));
    if (img.complete) measure(img);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const zoomable = Boolean(src) && !inLink;
  const openLightbox = () => setOpen(true);

  return (
    <>
      <img
        ref={ref}
        src={src}
        alt={alt}
        loading="lazy"
        width={width}
        className={cn(className, zoomable && styles.zoomable)}
        style={displayWidth ? { ...style, width: `${displayWidth}px` } : style}
        onLoad={(e) => measure(e.currentTarget)}
        {...(zoomable
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': alt ? `Enlarge image: ${alt}` : 'Enlarge image',
              onClick: openLightbox,
              onKeyDown: (e: React.KeyboardEvent<HTMLImageElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLightbox();
                }
              },
            }
          : {})}
        {...props}
      />
      {zoomable && src && (
        <ImageLightbox src={src} alt={alt} open={open} onOpenChange={setOpen} />
      )}
    </>
  );
};

export default ContentImage;
